// src/components/TempoModal.tsx
import { useState, useEffect, FormEvent, useRef } from 'react'
import { CheckCircle } from 'lucide-react'
import { useNotesStore } from '~/stores/useNotesStore'
import { Dialog } from '~/components/ui/Dialog'
import { Label } from '~/components/ui/Label'
import { Slider } from '~/components/ui/Slider'

interface TempoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

// BPM limits based on MPC/OP-XY (typically 30-300 BPM)
const MIN_BPM = 30;
const MAX_BPM = 300;

// Swing limits: -50 to +50 with 0 as default
const MIN_SWING = -50;
const MAX_SWING = 50;

export default function TempoModal({ isOpen, onClose }: TempoModalProps) {
  const bpm = useNotesStore((state) => state.bpm);
  const swing = useNotesStore((state) => state.swing);
  const setBpm = useNotesStore((state) => state.setBpm);
  const setSwing = useNotesStore((state) => state.setSwing);

  // Local state for BPM input to prevent updates on every keystroke
  const [bpmInput, setBpmInput] = useState(bpm.toString());

  // State for showing tick icon when BPM is updated
  const [showTick, setShowTick] = useState(false);
  const tickTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Sync local state when modal opens or store value changes
  useEffect(() => {
    setBpmInput(bpm.toString());
  }, [bpm, isOpen]);

  // Clear timeout on unmount
  useEffect(() => {
    return () => {
      if (tickTimeoutRef.current) {
        clearTimeout(tickTimeoutRef.current);
      }
    };
  }, []);

  // Show tick icon briefly when BPM is updated
  const showTickBriefly = () => {
    // Clear any existing timeout
    if (tickTimeoutRef.current) {
      clearTimeout(tickTimeoutRef.current);
    }

    setShowTick(true);

    // Hide tick after 1 second
    tickTimeoutRef.current = setTimeout(() => {
      setShowTick(false);
    }, 1000);
  };

  // Handle BPM blur - only update store when field loses focus
  const handleBpmBlur = () => {
    const value = parseFloat(bpmInput);

    // Validate and clamp to min/max
    if (!isNaN(value)) {
      const clampedValue = Math.max(MIN_BPM, Math.min(MAX_BPM, value));
      const changed = clampedValue !== bpm;
      setBpm(clampedValue);
      setBpmInput(clampedValue.toString());
      if (changed) {
        showTickBriefly();
      }
    } else {
      // Invalid input - reset to current store value
      setBpmInput(bpm.toString());
    }
  };

  // Handle BPM input change - only validate format, don't update store
  const handleBpmChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    // Only allow digits and a single dot
    if (/^\d*\.?\d*$/.test(value) || value === '') {
      setBpmInput(value);
    }
  };

  // Handle form submit (Enter key)
  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    handleBpmBlur();
  };

  // Handle BPM keyboard controls (Arrow keys)
  const handleBpmKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();

      const currentBpm = useNotesStore.getState().bpm;
      const increment = e.shiftKey ? 0.01 : 1;
      const delta = e.key === 'ArrowUp' ? increment : -increment;
      const newBpm = currentBpm + delta;

      // Clamp to min/max and round to 2 decimal places for precision
      const clampedBpm = Math.max(MIN_BPM, Math.min(MAX_BPM, newBpm));
      const roundedBpm = Math.round(clampedBpm * 100) / 100;

      if (roundedBpm !== currentBpm) {
        setBpm(roundedBpm);
        setBpmInput(roundedBpm.toString());
        showTickBriefly();
      }
    }
  };

  // Handle swing change - immediate effect during playback
  const handleSwingChange = (value: number) => {
    setSwing(value);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <Dialog.Content className="max-w-[95vw] sm:max-w-md p-4 sm:p-8" showCloseButton={true}>
        {/* Header */}
        <Dialog.Title className="mb-4">
          Tempo & Swing
        </Dialog.Title>

        {/* BPM Section */}
        <form onSubmit={handleSubmit} className="mb-6">
          <Label htmlFor="bpm-input" className="block mb-2 text-gray-300">
            BPM ({MIN_BPM}-{MAX_BPM})
          </Label>
          <div className="flex items-center gap-3">
            <input
              type="text"
              inputMode="decimal"
              id="bpm-input"
              className="p-3 max-w-32 bg-gray-800 border border-gray-700 text-white text-lg rounded-lg focus:ring-purple-500 focus:border-purple-500 placeholder-gray-500"
              placeholder={`${MIN_BPM}-${MAX_BPM}`}
              value={bpmInput}
              onChange={handleBpmChange}
              onBlur={handleBpmBlur}
              onKeyDown={handleBpmKeyDown}
              autoComplete="off"
            />
            <CheckCircle
              size={20}
              className={`text-green-500 transition-opacity duration-300 ${showTick ? 'opacity-100' : 'opacity-0'}`}
              aria-label="BPM updated"
            />
          </div>
        </form>

        {/* Swing Section */}
        <div className="mb-4">
          <Label htmlFor="swing-slider" className="block mb-3 text-gray-300">
            Swing
          </Label>
          <Slider
            value={[swing]}
            min={MIN_SWING}
            max={MAX_SWING}
            step={1}
            onChange={handleSwingChange}
            className="mb-2"
          />
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
